/**
 * Integration Card Component
 * Displays integration details with connection and action selection UI
 */

import { useState, useMemo, useCallback, memo, useEffect, useRef } from "react";
import { Plug, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ActionSelector } from "./action-selector";
import { IntegrationFormDialog } from "@/components/integrations/integration-form-dialog";
import { integrationService } from "@/services/integration-service";
import type {
  IntegrationCardProps,
  ConnectedIntegrationInstance,
  IntegrationAction,
} from "./types";

/**
 * IntegrationCard component for displaying individual integration details
 *
 * @param integration - The integration data
 * @param selectedIntegration - Currently selected integration data
 * @param onConnect - Callback when integration is connected
 * @param onDisconnect - Callback when integration is disconnected
 * @param onActionsChange - Callback when actions selection changes
 * @param onConnectionInstanceChange - Callback when connection instance selection changes
 * @param className - Additional CSS classes
 */
const IntegrationCard = memo(function IntegrationCard({
  integration,
  selectedIntegration,
  onConnect,
  onActionsChange,
  onConnectionInstanceChange,
  className,
}: IntegrationCardProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [dynamicActions, setDynamicActions] = useState<IntegrationAction[]>([]);
  const [isLoadingActions, setIsLoadingActions] = useState(false);
  const [loadedIntegrationId, setLoadedIntegrationId] = useState<string | null>(
    null
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check if integration is connected and get connected instances
  const isConnected = integration.connections.some((conn) => conn.isConnected);

  // Memoize expensive connected instances computation
  const connectedInstances: ConnectedIntegrationInstance[] = useMemo(
    () =>
      integration.connections
        .filter((conn) => conn.isConnected && conn.connectionDetails)
        .flatMap(
          (conn) =>
            conn.connectionDetails?.map((detail) => ({
              id: detail.id,
              displayName: detail.config?.name ?? "",
              resourceId: detail.id,
              connectionId: conn.id,
              createdAt: new Date(detail.createdAt!),
              config: detail.config,
            })) || []
        ),
    [integration.connections]
  );

  // Memoize currently selected connection instance
  const selectedConnectionInstance = useMemo(
    () =>
      connectedInstances.find(
        (instance) =>
          instance.resourceId ===
          (selectedIntegration as any)?.selectedConnectionInstance
      ) || connectedInstances[0],
    [connectedInstances, (selectedIntegration as any)?.selectedConnectionInstance]
  );

  /**
   * Handle opening the configuration dialog
   */
  const handleConnect = useCallback(() => {
    if (isConnected || isConnecting) return;
    setShowConfigDialog(true);
  }, [isConnected, isConnecting]);

  /**
   * Handle successful integration configuration
   */
  const handleConfigSuccess = useCallback(async () => {
    // Find the first available connection type
    const availableConnection = integration.connections[0];
    if (!availableConnection) return;

    setIsConnecting(true);
    try {
      // In a real implementation, this would make an API call to connect with the configuration
      // For now, we'll just simulate the connection
      await new Promise((resolve) => setTimeout(resolve, 1000));
      onConnect(integration.id, availableConnection.id);
    } catch (error) {
      console.error("Failed to connect integration:", error);
    } finally {
      setIsConnecting(false);
    }
  }, [integration.connections, integration.id, onConnect]);

  /**
   * Handle actions change
   */
  const handleActionsChange = useCallback(
    (actions: string[]) => {
      onActionsChange(integration.id, actions);
    },
    [integration.id, onActionsChange]
  );

  /**
   * Handle connection instance selection change
   */
  const handleConnectionInstanceChange = useCallback(
    (instanceId: string) => {
      onConnectionInstanceChange(integration.id, instanceId);
    },
    [integration.id, onConnectionInstanceChange]
  );

  /**
   * Load dynamic actions for this integration with deduplication and cancellation support
   */
  const loadActions = useCallback(
    async (integrationId: string) => {
      // Prevent multiple simultaneous calls for the same integration
      if (isLoadingActions || loadedIntegrationId === integrationId) {
        return;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      const currentController = abortControllerRef.current;

      setIsLoadingActions(true);
      try {
        const actions =
          await integrationService.fetchIntegrationActions(integrationId);

        // Only update state if this request wasn't cancelled
        if (!currentController.signal.aborted) {
          setDynamicActions(actions);
          setLoadedIntegrationId(integrationId);
        }
      } catch (error) {
        // Only handle errors if request wasn't cancelled
        if (!currentController.signal.aborted) {
          console.error(
            "Failed to load dynamic actions for integration:",
            integrationId,
            error
          );
          // Set empty actions array on error
          setDynamicActions([]);
          setLoadedIntegrationId(integrationId);
        }
      } finally {
        // Only update loading state if request wasn't cancelled
        if (!currentController.signal.aborted) {
          setIsLoadingActions(false);
        }
      }
    },
    [isLoadingActions, loadedIntegrationId]
  );

  /**
   * Trigger function to manually load actions for the integration
   */
  const triggerLoadActions = useCallback(() => {
    if (integration.id && loadedIntegrationId !== integration.id) {
      loadActions(integration.id);
    }
  }, [integration.id, loadedIntegrationId, loadActions]);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Fallback to icon if image fails to load
      const target = e.target as HTMLImageElement;
      target.style.display = "none";
      target.nextElementSibling?.classList.remove("hidden");
    },
    []
  );

  /**
   * Auto-load actions for already connected integrations on initial render
   */
  useEffect(() => {
    // If integration is already connected and we haven't loaded actions yet, load them automatically
    if (
      isConnected &&
      integration.id &&
      loadedIntegrationId !== integration.id
    ) {
      loadActions(integration.id);
    }
  }, [isConnected, integration.id, loadedIntegrationId, loadActions]);

  /**
   * Cleanup effect to cancel pending requests on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Get available actions for this integration
   * Uses dynamic actions only
   */
  const availableActions = useMemo(() => {
    return dynamicActions;
  }, [dynamicActions]);

  return (
    <Card
      className={cn(
        "transition-all duration-200 hover:shadow-md",
        isConnected && "ring-2 ring-primary/20",
        className
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            {integration.logoUrl ? (
              <img
                src={integration.logoUrl}
                alt={`${integration.name} logo`}
                className="h-8 w-8 rounded-md object-contain"
                onError={handleImageError}
              />
            ) : null}
            <Plug
              className={cn(
                "h-8 w-8 text-muted-foreground",
                integration.logoUrl && "hidden"
              )}
            />

            <div>
              <CardTitle className="text-base">{integration.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                {isConnected ? (
                  <Badge variant="secondary" className="text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    {connectedInstances.length > 1
                      ? `Connected (${connectedInstances.length})`
                      : "Connected"}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    Available
                  </Badge>
                )}

                {!integration.availableForAgent && (
                  <Badge variant="destructive" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Not for agents
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <CardDescription className="text-xs line-clamp-2">
          {integration.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Integration Details & Configuration */}
          {isConnected ? (
            <div className="space-y-3">
              {/* Connection Instance Selection - Always show for connected integrations */}
              {connectedInstances.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {connectedInstances.length === 1
                      ? "Connection:"
                      : "Select Connection:"}
                  </label>
                  <Select
                    value={selectedConnectionInstance?.resourceId || ""}
                    onValueChange={handleConnectionInstanceChange}
                  >
                    <SelectTrigger className="w-full !h-12">
                      <SelectValue placeholder="Choose connection..." />
                    </SelectTrigger>
                    <SelectContent>
                      {connectedInstances.map((instance) => (
                        <SelectItem
                          key={instance.resourceId}
                          value={instance.resourceId}
                        >
                          <div className="flex flex-col items-start">
                            <span className="font-medium">
                              {instance.displayName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Connected{" "}
                              {new Date(
                                instance.createdAt
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Action Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {isLoadingActions ? "Loading Actions..." : "Choose Actions:"}
                </label>
                {isLoadingActions ? (
                  <div className="h-10 bg-muted rounded-md animate-pulse" />
                ) : availableActions.length > 0 ? (
                  <ActionSelector
                    integrationId={integration.id}
                    availableActions={availableActions}
                    selectedActions={(selectedIntegration as any)?.selectedActions || []}
                    onActionsChange={handleActionsChange}
                  />
                ) : loadedIntegrationId !== integration.id ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={triggerLoadActions}
                    className="w-full"
                  >
                    Load Available Actions
                  </Button>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    No actions available
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Connect Button */}
              <Button
                variant="default"
                size="sm"
                onClick={handleConnect}
                disabled={isConnecting || !integration.availableForAgent}
                className="w-full"
              >
                {isConnecting ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Plug className="h-3 w-3 mr-2" />
                    Connect
                  </>
                )}
              </Button>

              {!integration.availableForAgent && (
                <p className="text-xs text-muted-foreground">
                  This integration is not available for AI agents.
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Integration Configuration Dialog */}
      <IntegrationFormDialog
        open={showConfigDialog}
        integration={integration}
        onClose={() => setShowConfigDialog(false)}
        onSuccess={handleConfigSuccess}
      />
    </Card>
  );
});

export { IntegrationCard };
